import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Compass,
  LifeBuoy,
  Menu,
  Orbit,
  Sparkles,
  X,
} from 'lucide-react';
import {
  DEFAULT_ISLAND_KEY,
  ISLAND_KEYS,
  SITE_NAME,
  getIslandConfig,
  getSiteBackgroundSpec,
  getIslandUrl,
} from 'alakai-aloha-shared';
import { PublicSiteBackground } from 'alakai-aloha-shared/public-ui';
import { APEX_FUTURE_PRODUCT_LINKS, getApexIslandEntries } from '../../lib/apexContent';
import AppLink from '../shared/AppLink';

const COMPANY_LINKS = [
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/about' },
  { label: 'Support', href: '/support' },
];

const LEGAL_LINKS = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
];

const MINIMAL_FOOTER_LINKS = [
  { label: 'Blog', href: '/blog' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact Us', href: '/support' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Use', href: '/terms' },
];

function ApexShellLink({ href, className, children, onClick }) {
  if (href.startsWith('/#') || href.startsWith('#')) {
    return (
      <a href={href} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }

  return (
    <AppLink to={href} className={className} onClick={onClick}>
      {children}
    </AppLink>
  );
}

export default function ApexShell({
  children,
  currentPath,
  headerCtaLabel = 'Explore Kauai',
  headerCtaHref = getIslandUrl(DEFAULT_ISLAND_KEY, '/'),
  onHeaderCtaClick,
  headerHomeHref = '/',
  headerLogoSrc = '/brand/logos/apex/horizontal/white.svg',
  headerLogoAlt = `${SITE_NAME} logo`,
  footerLogoSrc = '/brand/logos/apex/full/white.svg',
  footerLogoAlt = `${SITE_NAME} logo`,
  footerCtaLabel = 'Explore Kauai',
  footerCtaHref = getIslandUrl(DEFAULT_ISLAND_KEY, '/'),
  onFooterCtaClick,
  backgroundTheme = getSiteBackgroundSpec('apex'),
  minimalFooter = false,
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const islandLinks = useMemo(
    () => getApexIslandEntries().map(({ key, title, href }) => ({
      label: key === 'big_island' ? 'Big Island' : title,
      href,
    })),
    [],
  );

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [currentPath]);

  return (
    <div className="apex-shell relative min-h-screen overflow-hidden bg-[#071219] text-white">
      <PublicSiteBackground backgroundTheme={backgroundTheme} />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)',
          backgroundSize: '120px 120px',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.15))',
        }}
      />

      <header className="sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-6">
          <ApexShellLink href={headerHomeHref} className="flex shrink-0 items-center">
            <img src={headerLogoSrc} alt={headerLogoAlt} className="h-16 w-auto sm:h-20" />
          </ApexShellLink>

          <nav className="hidden items-center gap-2 lg:flex" aria-label="Apex island navigation">
            {islandLinks.map((link) => (
              <ApexShellLink
                key={link.href}
                href={link.href}
                className="rounded-full px-3 py-2 text-sm font-semibold text-white/78 transition hover:bg-white/5 hover:text-white"
              >
                {link.label}
              </ApexShellLink>
            ))}
          </nav>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((current) => !current)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white lg:hidden"
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle apex navigation"
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {isMobileMenuOpen ? (
          <div className="border-t border-white/10 px-5 pb-6 pt-4 lg:hidden">
            <div className="mx-auto max-w-7xl">
              <div className="grid gap-2">
                {islandLinks.map((link) => (
                  <ApexShellLink
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-white"
                  >
                    {link.label}
                  </ApexShellLink>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </header>

      <main className="relative z-10 flex-1 pb-16 pt-10">{children}</main>

      {minimalFooter ? (
        <footer className="relative z-10 px-6 pb-5 pt-5 text-center text-xs text-white/78 sm:text-sm">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span>© 2026 Alakai Aloha</span>
            <span>|</span>
            {MINIMAL_FOOTER_LINKS.map((link, index) => (
              <React.Fragment key={link.href}>
                <ApexShellLink href={link.href} className="underline-offset-2 hover:text-white hover:underline">
                  {link.label}
                </ApexShellLink>
                {index < MINIMAL_FOOTER_LINKS.length - 1 ? <span>|</span> : null}
              </React.Fragment>
            ))}
          </div>
        </footer>
      ) : (
        <footer className="relative z-10 border-t border-white/10 bg-black/20">
          <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-16">
            <div className="grid gap-12 xl:grid-cols-[1.45fr_repeat(3,minmax(0,1fr))]">
              <div className="max-w-xl">
                <img src={footerLogoSrc} alt={footerLogoAlt} className="h-20 w-auto" />
                <h2 className="apex-display mt-5 text-3xl font-semibold text-white sm:text-4xl">
                  The parent brand for island-by-island Hawaii travel.
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/68 sm:text-base">
                  Apex owns the shared brand, legal, and support layer today. Island editions own the deep local guides.
                  The shared planning products come next.
                </p>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <ApexShellLink
                    href={footerCtaHref}
                    onClick={onFooterCtaClick}
                    className="apex-button-primary"
                  >
                    <span>{footerCtaLabel}</span>
                    <ArrowRight className="h-4 w-4" />
                  </ApexShellLink>
                  <ApexShellLink href="/support" className="apex-button-secondary">
                    <LifeBuoy className="h-4 w-4" />
                    <span>Contact support</span>
                  </ApexShellLink>
                </div>
              </div>

              <div>
                <p className="apex-kicker">
                  <Compass className="h-3.5 w-3.5" />
                  Island Editions
                </p>
                <div className="mt-4 grid gap-3">
                  {islandLinks.map((link) => (
                    <ApexShellLink key={link.href} href={link.href} className="apex-text-link block text-sm font-semibold">
                      {link.label}
                    </ApexShellLink>
                  ))}
                </div>
              </div>

              <div>
                <p className="apex-kicker">
                  <Orbit className="h-3.5 w-3.5" />
                  Future Products
                </p>
                <div className="mt-4 grid gap-4">
                  {APEX_FUTURE_PRODUCT_LINKS.map((link) => (
                    <ApexShellLink key={link.href} href={link.href} className="block">
                      <div className="apex-text-link text-sm font-semibold">{link.label}</div>
                      <p className="mt-1 text-sm leading-relaxed text-white/58">{link.description}</p>
                    </ApexShellLink>
                  ))}
                </div>
              </div>

              <div>
                <p className="apex-kicker">
                  <Sparkles className="h-3.5 w-3.5" />
                  Company
                </p>
                <div className="mt-4 grid gap-3">
                  {COMPANY_LINKS.concat(LEGAL_LINKS).map((link) => (
                    <ApexShellLink key={link.href} href={link.href} className="apex-text-link block text-sm font-semibold">
                      {link.label}
                    </ApexShellLink>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-12 border-t border-white/10 pt-6">
              <div className="flex flex-wrap gap-2">
                {ISLAND_KEYS.map((islandKey) => {
                  const island = getIslandConfig(islandKey);
                  return (
                    <span key={islandKey} className="apex-chip">
                      {island.shortTitle}
                    </span>
                  );
                })}
              </div>

              <div className="mt-5 flex flex-col gap-3 text-sm text-white/54 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <p>{`© ${new Date().getFullYear()} ${SITE_NAME}`}</p>
                <div className="flex flex-wrap gap-4">
                  {LEGAL_LINKS.map((link) => (
                    <ApexShellLink key={link.href} href={link.href} className="apex-text-link text-sm">
                      {link.label}
                    </ApexShellLink>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
